import styled, { css } from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import type { PriceValue } from '../../../configurator/controls';
import { Body2 } from '../../components/typography';
import { useRenderPrice } from '../../utils/use-render-price';
import { useWidgetDimensions } from '../../widget-dimensions-provider';

type ServiceCardFullPriceProps = {
  price: PriceValue;
  duration: number;
  displayPrice: boolean;
};

export const ServiceCardFullPrice = ({
  price,
  duration,
  displayPrice
}: ServiceCardFullPriceProps) => {
  const { t } = useLingui();
  const { isMobile } = useWidgetDimensions();
  const servicePrice = useRenderPrice(price);

  const serviceDuration = [120, 240].includes(duration)
    ? t`${duration / 60} hours`
    : t`${duration} minutes`;

  return (
    <Container _isMobile={isMobile}>
      {displayPrice && (
        <>
          <Price>{servicePrice}</Price>
          <Dot>·</Dot>
        </>
      )}
      <Duration>{serviceDuration}</Duration>
    </Container>
  );
};

const Container = styled(Body2).attrs({
  className: createReadableClass({
    component: 'service-card-full-price',
    element: 'container'
  })
})<{ _isMobile: boolean }>`
  display: flex;
  gap: 4px;
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      font-size: 14px;
    `}
`;

const Dot = styled.span.attrs({
  className: createReadableClass({
    component: 'service-card-full-price',
    element: 'dot'
  })
})`
  font-weight: 600;
`;

const Price = styled.span.attrs({
  className: createReadableClass({
    component: 'service-card-full-price',
    element: 'price'
  })
})`
  font-weight: 600;
`;

const Duration = styled.span.attrs({
  className: createReadableClass({
    component: 'service-card-full-price',
    element: 'duration'
  })
})`
  opacity: 0.5;
  font-weight: 400;
`;
